import { mapReferralItem } from 'modules/session/mapBasicSignup';
import ReferralsApi from './repository';

/** Texto exacto del backend cuando el cliente aún no tiene programa. */
export const NO_REFERRAL_INFO_MESSAGE = 'Cliente aun no tiene información de Referidos';

/** Código de dominio para lista vacía (no es un fallo de carga). */
export const NO_REFERRAL_INFO_CODE = 'NO_REFERRAL_INFO';

/**
 * Extrae el mensaje de error de la forma normalizada por ApiFetch.
 * ApiFetch produce: { statusCode, data: { status, error }, rawData }.
 * @param {*} error
 * @returns {*}
 */
export function getNormalizedApiErrorMessage(error) {
  if (error?.data && Object.prototype.hasOwnProperty.call(error.data, 'error')) {
    return error.data.error;
  }
  if (error?.rawData && Object.prototype.hasOwnProperty.call(error.rawData, 'error')) {
    return error.rawData.error;
  }
  return undefined;
}

/**
 * Solo el 400 con el mensaje documentado cuenta como “sin información”.
 * Cualquier otro BAD_REQUEST sigue siendo error.
 * Usa la forma real de ApiFetch (data.error / rawData.error), no error.message.
 * @param {*} error
 * @returns {boolean}
 */
export function isNoReferralInfoError(error) {
  return error?.statusCode === 400
    && getNormalizedApiErrorMessage(error) === NO_REFERRAL_INFO_MESSAGE;
}

/**
 * Mapea la respuesta del endpoint de código propio.
 * @param {*} response
 * @returns {{ ownReferralCode: *, CodigoReferidor: * }}
 */
export function mapOwnReferrerCodeResponse(response) {
  const isObject = response && typeof response === 'object' && !Array.isArray(response);
  const hasCode = isObject
    && Object.prototype.hasOwnProperty.call(response, 'CodigoReferidor');

  if (!hasCode) {
    const error = new Error('UNEXPECTED_REFERRER_CODE_RESPONSE');
    error.code = 'UNEXPECTED_REFERRER_CODE_RESPONSE';
    throw error;
  }

  return {
    ownReferralCode: response.CodigoReferidor,
    CodigoReferidor: response.CodigoReferidor,
  };
}

/**
 * Mapea la respuesta de POST /users/clients/referral-code (solo lista).
 * No exige CodigoReferidor en la raíz.
 * @param {*} response
 * @returns {{ referrals: object[] }}
 */
export function mapReferralsListResponse(response) {
  const isObject = response && typeof response === 'object' && !Array.isArray(response);
  const hasList = isObject && Array.isArray(response.referidos);

  if (!hasList) {
    const error = new Error('UNEXPECTED_REFERRAL_RESPONSE');
    error.code = 'UNEXPECTED_REFERRAL_RESPONSE';
    throw error;
  }

  return {
    referrals: response.referidos.map(mapReferralItem),
  };
}

/** Lee el código propio desde el endpoint dedicado. */
export async function fetchOwnReferrerCode() {
  const response = await ReferralsApi.getReferrerCode();
  return mapOwnReferrerCodeResponse(response);
}

/**
 * Lee la lista de referidos.
 * El 400 conocido se propaga como NO_REFERRAL_INFO_CODE (lista vacía en UI).
 */
export async function fetchReferrals() {
  try {
    const response = await ReferralsApi.getReferralCode();
    return mapReferralsListResponse(response);
  } catch (error) {
    if (isNoReferralInfoError(error)) {
      const domainError = new Error(NO_REFERRAL_INFO_CODE);
      domainError.code = NO_REFERRAL_INFO_CODE;
      throw domainError;
    }
    throw error;
  }
}
